<!--
ThemeToggle Component

A reusable theme switcher button that toggles between light and dark modes.
Uses the useTheme composable for state management and persistence.

Features:
- Icon animation on theme switch
- Keyboard accessible (Enter/Space to toggle)
- ARIA attributes for screen readers
- Smooth transitions
- Size variants (sm, md, lg)
- Optional text label

Usage:
```vue
<ThemeToggle />
<ThemeToggle size="lg" />
<ThemeToggle :show-label="true" />
```

The theme preference is persisted in localStorage and respects system
preferences when set to 'system' mode.
-->

<script setup lang="ts">
import { computed } from 'vue'
import { useTheme } from '@/composables/useTheme'
import { useLocale } from '@/composables/useLocale'
import AppIcon from '@/components/ui/AppIcon.vue'

interface Props {
  /**
   * Size variant of the toggle button
   */
  size?: 'sm' | 'md' | 'lg'
  /**
   * Whether to show a label next to the icon
   */
  showLabel?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  showLabel: false,
})

const { isDark, toggleTheme } = useTheme()
const { t } = useLocale()

const sizeClasses = computed(() => {
  const sizes = {
    sm: 'tw-w-8 tw-h-8 tw-text-sm',
    md: 'tw-w-9 tw-h-9 tw-text-base',
    lg: 'tw-w-11 tw-h-11 tw-text-lg',
  }
  return sizes[props.size]
})

const iconName = computed(() => {
  return isDark.value ? 'solar:sun-bold' : 'solar:moon-bold'
})

const label = computed(() => {
  return isDark.value ? t('settings.theme.light') : t('settings.theme.dark')
})

const ariaLabel = computed(() => {
  return isDark.value ? t('settings.theme.switchToLight') : t('settings.theme.switchToDark')
})

const handleToggle = (): void => {
  toggleTheme()
}

const handleKeyDown = (event: KeyboardEvent): void => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    handleToggle()
  }
}
</script>

<template>
  <button
    type="button"
    :class="[
      sizeClasses,
      'tw-flex tw-items-center tw-justify-center tw-gap-2',
      'tw-rounded-lg tw-border-0',
      'tw-bg-surface-100 tw-text-surface-600',
      'hover:tw-bg-surface-200',
      'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-500',
      'active:tw-scale-95',
      'tw-transition-all tw-duration-200',
      'tw-cursor-pointer',
    ]"
    :aria-label="ariaLabel"
    :aria-pressed="isDark"
    role="switch"
    @click="handleToggle"
    @keydown="handleKeyDown"
  >
    <AppIcon
      :name="iconName"
      :class="[
        'tw-transition-transform tw-duration-300',
        isDark ? 'tw-rotate-0' : 'tw-rotate-0',
      ]"
    />
    <span v-if="showLabel" class="tw-text-sm tw-font-medium">
      {{ label }}
    </span>
  </button>
</template>
